import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'
import type { BlissBasicConfiguratorCatalogStepId } from '../utils/blissBasicConfiguratorDefaults'
import { defaultConfiguratorStepDefs } from '../utils/blissBasicConfiguratorDefaults'

const log = logger.module('blissBasicOptionItems')

export type BlissBasicOptionItemRow = {
  id: string
  tenant_id: string
  step_id: string
  choice_key: string
  label: string
  detail: string | null
  price_usd: number | null
  price_high_usd: number | null
  price_label: string | null
  gallery_photo_id: string | null
  sort_order: number
  active: boolean
  in_stock: boolean
  created_at: string
  updated_at: string
}

export async function fetchBlissBasicOptionItems(tenantId: string): Promise<BlissBasicOptionItemRow[]> {
  const { data, error } = await supabase
    .from('bliss_basic_option_items')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('step_id')
    .order('sort_order', { ascending: true })
  if (error) {
    log.error('fetch option items', error)
    throw error
  }
  return (data ?? []) as BlissBasicOptionItemRow[]
}

export async function upsertBlissBasicOptionItem(
  tenantId: string,
  input: Partial<BlissBasicOptionItemRow> & {
    step_id: string
    choice_key: string
    label: string
  },
): Promise<BlissBasicOptionItemRow> {
  const payload = {
    tenant_id: tenantId,
    step_id: input.step_id,
    choice_key: input.choice_key,
    label: input.label,
    detail: input.detail ?? null,
    price_usd: input.price_usd ?? null,
    price_high_usd: input.price_high_usd ?? null,
    price_label: input.price_label ?? 'Starting at',
    gallery_photo_id: input.gallery_photo_id ?? null,
    sort_order: input.sort_order ?? 0,
    active: input.active ?? true,
    in_stock: input.in_stock ?? true,
  }
  if (input.id) {
    const { data, error } = await supabase
      .from('bliss_basic_option_items')
      .update(payload)
      .eq('id', input.id)
      .eq('tenant_id', tenantId)
      .select()
      .single()
    if (error) throw error
    return data as BlissBasicOptionItemRow
  }
  const { data, error } = await supabase.from('bliss_basic_option_items').insert(payload).select().single()
  if (error) throw error
  return data as BlissBasicOptionItemRow
}

/** Persist display order for one configurator step (0-based sort_order). */
export async function reorderBlissBasicOptionItemsForStep(
  tenantId: string,
  stepId: string,
  orderedIds: string[],
): Promise<void> {
  const results = await Promise.all(
    orderedIds.map((id, index) =>
      supabase
        .from('bliss_basic_option_items')
        .update({ sort_order: index })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .eq('step_id', stepId),
    ),
  )
  const error = results.find((r) => r.error)?.error
  if (error) {
    log.error('reorder option items', error)
    throw error
  }
}

export async function deleteBlissBasicOptionItem(tenantId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('bliss_basic_option_items')
    .delete()
    .eq('id', id)
    .eq('tenant_id', tenantId)
  if (error) throw error
}

/** Seed tenant table from built-in defaults (skip existing choice_keys). */
export async function seedBlissBasicOptionItemsFromDefaults(tenantId: string): Promise<number> {
  const existing = await fetchBlissBasicOptionItems(tenantId)
  const have = new Set(existing.map((r) => `${r.step_id}:${r.choice_key}`))
  let inserted = 0
  for (const step of defaultConfiguratorStepDefs()) {
    let order = 0
    for (const item of step.items) {
      const key = `${step.id}:${item.choiceKey}`
      if (have.has(key)) continue
      await upsertBlissBasicOptionItem(tenantId, {
        step_id: step.id,
        choice_key: item.choiceKey,
        label: item.label,
        detail: item.detail,
        price_usd: item.priceUsd,
        price_high_usd: item.priceHighUsd,
        price_label: item.priceLabel,
        sort_order: order++,
      })
      inserted++
    }
  }
  return inserted
}

export function slugifyChoiceKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 48)
}

export type { BlissBasicConfiguratorCatalogStepId }
